import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiChevronLeft, FiToggleLeft, FiToggleRight, FiSearch,
  FiCheck, FiX, FiDatabase, FiTrash2, FiMinusCircle,
} from 'react-icons/fi';
import request from '../utils/request';

const BADGE = {
  active: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  inactive: { bg: 'bg-zinc-50', text: 'text-zinc-600', border: 'border-zinc-200', dot: 'bg-zinc-400' },
  restricted: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
};

const EMPTY_FORM = {
  display_label: '',
  retrieval_aliases: '',
  retrieval_category: 'general',
  answer_mode: 'descriptive',
  retrieval_hint: '',
  boost_weight: '2',
  guidebook_enabled: false,
};

function mappingKey(mapping) {
  if (!mapping) return '';
  return `${mapping.source_collection || ''}:${mapping.source_field || ''}:${mapping.target_table || ''}:${mapping.target_field || ''}`;
}

function mergeEffectiveMappings(globalMappings = [], hostMappings = []) {
  const globalByKey = new Map(globalMappings.map((mapping) => [mappingKey(mapping), mapping]));
  const hostByKey = new Map(hostMappings.map((mapping) => [mappingKey(mapping), mapping]));
  const merged = globalMappings.map((globalMapping) => {
    const key = mappingKey(globalMapping);
    const hostOverride = hostByKey.get(key);
    if (!hostOverride) {
      return {
        ...globalMapping,
        _effectiveSource: 'global',
      };
    }
    return {
      ...globalMapping,
      ...hostOverride,
      _effectiveSource: 'host',
      _globalMapping: globalMapping,
      _hostOverride: hostOverride,
    };
  });
  hostMappings.forEach((hostMapping) => {
    const key = mappingKey(hostMapping);
    if (globalByKey.has(key)) return;
    merged.push({
      ...hostMapping,
      _effectiveSource: 'host',
      _globalMapping: null,
      _hostOverride: hostMapping,
    });
  });
  return merged;
}

function buildFormState(mapping) {
  if (!mapping) return EMPTY_FORM;
  return {
    display_label: mapping.display_label || '',
    retrieval_aliases: Array.isArray(mapping.retrieval_aliases) ? mapping.retrieval_aliases.join(', ') : '',
    retrieval_category: mapping.retrieval_category || 'general',
    answer_mode: mapping.answer_mode || 'descriptive',
    retrieval_hint: mapping.retrieval_hint || '',
    boost_weight: String(mapping.boost_weight ?? 2),
    guidebook_enabled: mapping.guidebook_enabled === true,
  };
}

function parseAliases(value) {
  return [...new Set(
    String(value || '')
      .split(',')
      .map(item => item.trim())
      .filter(Boolean),
  )];
}

function FieldCard({
  title,
  badge,
  fields,
  totalCount = null,
  onToggle,
  onSelect,
  selectedId,
  previewByTarget = null,
  showPreviewColumn = false,
  emptyText = 'No fields in this category',
}) {
  const b = BADGE[badge] || BADGE.inactive;
  return (
    <div className="bg-white rounded-2xl border border-zinc-100 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${b.dot}`} />
          <h3 className="text-sm font-semibold text-zinc-900">{title}</h3>
        </div>
        <span className={`text-xs font-medium px-2.5 py-1 rounded-lg ${b.bg} ${b.text} border ${b.border}`}>
          {Number.isFinite(totalCount) ? `${fields.length}/${totalCount}` : fields.length}
        </span>
      </div>
      {fields.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-zinc-400">{emptyText}</div>
      ) : (
        <div className="divide-y divide-zinc-50 max-h-[400px] overflow-y-auto">
          {fields.map((field) => (
            <div
              key={field._id || field.field_name || field.path}
              className={`flex items-center justify-between px-5 py-3 transition-colors ${
                selectedId === field._id ? 'bg-indigo-50/70' : 'hover:bg-zinc-50/60'
              }`}
            >
              <div className="min-w-0 flex-1 grid grid-cols-12 gap-3 items-start">
                <button
                  type="button"
                  onClick={() => onSelect?.(field)}
                  className={`${showPreviewColumn ? 'col-span-7' : 'col-span-12'} min-w-0 text-left`}
                >
                  <p className="text-sm font-medium text-zinc-800 truncate">{field.target_field || field.field_name || field.path}</p>
                  <p className="text-xs text-zinc-400 mt-0.5 truncate">
                    {field.source_collection && `${field.source_collection}.${field.source_field}`}
                    {field.exposure_level && ` · ${field.exposure_level}`}
                    {field.target_table && ` → ${field.target_table}`}
                  </p>
                </button>
                {showPreviewColumn && (
                  <div className="col-span-5 min-w-0">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">Current Value</p>
                    <p className="text-xs text-zinc-600 truncate">
                      {previewByTarget?.get(field.target_field) || '—'}
                    </p>
                  </div>
                )}
              </div>
              {onToggle && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggle(field);
                  }}
                  className="ml-3 flex-shrink-0"
                >
                  {field.enabled ? (
                    <FiToggleRight className="w-5 h-5 text-emerald-500" />
                  ) : (
                    <FiToggleLeft className="w-5 h-5 text-zinc-300" />
                  )}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function HostFeatureSettingsScreen() {
  const { hostId } = useParams();
  const navigate = useNavigate();
  const [host, setHost] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [globalMappings, setGlobalMappings] = useState([]);
  const [hostMappings, setHostMappings] = useState([]);
  const [policyRules, setPolicyRules] = useState([]);
  const [discoveredFields, setDiscoveredFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fieldSearch, setFieldSearch] = useState('');
  const [activeFieldTab, setActiveFieldTab] = useState('all');
  const [selectedMappingId, setSelectedMappingId] = useState(null);
  const [mappingForm, setMappingForm] = useState(EMPTY_FORM);
  const [savingMapping, setSavingMapping] = useState(false);
  const [metadataMode, setMetadataMode] = useState('global');
  const [metadataModalOpen, setMetadataModalOpen] = useState(false);
  const [applyingGuidebookAll, setApplyingGuidebookAll] = useState(false);
  const [customFields, setCustomFields] = useState([]);
  const [sectionFilter, setSectionFilter] = useState('all');
  const [toastMessage, setToastMessage] = useState('');
  const [hostProperties, setHostProperties] = useState([]);
  const [selectedPreviewPropertyId, setSelectedPreviewPropertyId] = useState('');
  const [previewByTargetField, setPreviewByTargetField] = useState(new Map());

  const loadMappings = useCallback(async () => {
    const [effectiveRes, globalRes, hostRes] = await Promise.all([
      request.get('/studio/api/field-mappings', { params: { scope: 'effective', hostId } }),
      request.get('/studio/api/field-mappings', { params: { scope: 'global' } }),
      request.get('/studio/api/field-mappings', { params: { scope: 'host', hostId } }),
    ]);

    const effectiveRaw = effectiveRes.data || [];
    const globals = globalRes.data || [];
    const hosts = hostRes.data || [];
    const effective = mergeEffectiveMappings(
      globals.length ? globals : effectiveRaw.filter(mapping => !mapping.host_id),
      hosts.length ? hosts : effectiveRaw.filter(mapping => mapping.host_id),
    );

    setGlobalMappings(globals);
    setHostMappings(hosts);
    setMappings(effective);
    return effective;
  }, [hostId]);

  const selectMapping = useCallback((mapping, options = {}) => {
    const { openModal = true } = options;
    if (!mapping?._id) return;
    setSelectedMappingId(mapping._id);
    setMappingForm(buildFormState(mapping));
    if (openModal) setMetadataModalOpen(true);
  }, []);

  const loadCustomFields = useCallback(async () => {
    if (!hostId) {
      setCustomFields([]);
      return [];
    }
    const response = await request.get(`/studio/api/hosts/${hostId}/custom-fields`).catch(() => ({ data: [] }));
    const rows = response.data || [];
    setCustomFields(rows);
    return rows;
  }, [hostId]);

  useEffect(() => {
    const load = async () => {
      try {
        const [hostsRes, policiesRes, propsFields, rawFields, hostFields] = await Promise.all([
          request.get('/studio/api/hosts'),
          request.get('/studio/api/policy-rules'),
          request.get('/studio/api/fields/properties').catch(() => ({ data: [] })),
          request.get('/studio/api/fields/rawdatas').catch(() => ({ data: [] })),
          request.get('/studio/api/fields/hosts').catch(() => ({ data: [] })),
        ]);
        const found = hostsRes.data.find(h => h._id === hostId);
        setHost(found || null);
        await loadMappings();
        await loadCustomFields();
        if (found?._id) {
          const propertiesRes = await request
            .get(`/studio/api/hosts/${found._id}/properties`)
            .catch(() => ({ data: [] }));
          const properties = propertiesRes.data || [];
          setHostProperties(properties);
          setSelectedPreviewPropertyId((previous) => (
            previous && properties.some((property) => property.uniqueId === previous)
              ? previous
              : (properties[0]?.uniqueId || '')
          ));
        }
        setPolicyRules(policiesRes.data || []);
        setDiscoveredFields([
          ...(propsFields.data || []).map(f => ({ ...f, _collection: 'properties' })),
          ...(rawFields.data || []).map(f => ({ ...f, _collection: 'rawdatas' })),
          ...(hostFields.data || []).map(f => ({ ...f, _collection: 'hosts' })),
        ]);
      } catch (err) {
        console.error('Failed to load host detail:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [hostId, loadCustomFields, loadMappings]);

  useEffect(() => {
    if (!toastMessage) return undefined;
    const timeout = setTimeout(() => setToastMessage(''), 3000);
    return () => clearTimeout(timeout);
  }, [toastMessage]);

  useEffect(() => {
    const loadPropertyPreview = async () => {
      if (!hostId || !selectedPreviewPropertyId) {
        setPreviewByTargetField(new Map());
        return;
      }
      try {
        const res = await request.get(`/studio/api/hosts/${hostId}/field-preview`, {
          params: { propertyUniqueId: selectedPreviewPropertyId },
        });
        const values = res.data?.values || {};
        setPreviewByTargetField(new Map(Object.entries(values)));
      } catch (err) {
        console.error('Failed to load host field preview:', err);
        setPreviewByTargetField(new Map());
      }
    };
    loadPropertyPreview();
  }, [hostId, selectedPreviewPropertyId]);

  const editableMappings = useMemo(
    () => mappings.filter(mapping => mapping.target_table && mapping.target_field),
    [mappings],
  );
  const searchQuery = useMemo(() => fieldSearch.trim().toLowerCase(), [fieldSearch]);
  const matchesMappingSearch = useCallback((mapping) => {
    if (!searchQuery) return true;
    const haystack = [
      mapping.target_field,
      mapping.display_label,
      mapping.source_collection,
      mapping.source_field,
      `${mapping.source_collection || ''}.${mapping.source_field || ''}`,
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(searchQuery);
  }, [searchQuery]);
  const matchesCustomSearch = useCallback((field) => {
    if (!searchQuery) return true;
    const haystack = [
      field.target_field,
      field.display_label,
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(searchQuery);
  }, [searchQuery]);

  const activeFieldsAll = useMemo(() => mappings.filter(mapping => mapping.enabled), [mappings]);
  const inactiveFieldsAll = useMemo(() => mappings.filter(mapping => !mapping.enabled), [mappings]);
  const activeGuidebookFieldsAll = useMemo(
    () => activeFieldsAll.filter(mapping => mapping.guidebook_enabled === true),
    [activeFieldsAll],
  );
  const activeFields = useMemo(() => activeFieldsAll.filter(matchesMappingSearch), [activeFieldsAll, matchesMappingSearch]);
  const inactiveFields = useMemo(() => inactiveFieldsAll.filter(matchesMappingSearch), [inactiveFieldsAll, matchesMappingSearch]);
  const activeGuidebookFields = useMemo(
    () => activeGuidebookFieldsAll.filter(matchesMappingSearch),
    [activeGuidebookFieldsAll, matchesMappingSearch],
  );
  const filteredCustomFields = useMemo(
    () => customFields.filter(matchesCustomSearch),
    [customFields, matchesCustomSearch],
  );
  const restrictedFields = useMemo(() => {
    const restrictedLevels = new Set(['internal_only', 'secret_restricted']);
    const filtered = policyRules.filter(rule => restrictedLevels.has(rule.exposure_level));
    if (!searchQuery) return filtered;
    return filtered.filter((rule) => (
      `${rule.field_name || ''} ${rule.exposure_level || ''}`.toLowerCase().includes(searchQuery)
    ));
  }, [policyRules, searchQuery]);
  const selectedMapping = useMemo(
    () => editableMappings.find(mapping => mapping._id === selectedMappingId) || null,
    [editableMappings, selectedMappingId],
  );
  const selectedMappingKey = useMemo(
    () => mappingKey(selectedMapping),
    [selectedMapping],
  );
  const selectedHostOverride = useMemo(
    () => hostMappings.find(mapping => mappingKey(mapping) === selectedMappingKey) || null,
    [hostMappings, selectedMappingKey],
  );
  const selectedGlobalMapping = useMemo(
    () => globalMappings.find(mapping => mappingKey(mapping) === selectedMappingKey) || null,
    [globalMappings, selectedMappingKey],
  );
  const hostOverrideDiverges = useMemo(() => {
    if (metadataMode !== 'host' || !selectedGlobalMapping) return false;
    const globalForm = buildFormState(selectedGlobalMapping);
    return (
      globalForm.display_label !== mappingForm.display_label
      || globalForm.retrieval_aliases !== mappingForm.retrieval_aliases
      || globalForm.retrieval_category !== mappingForm.retrieval_category
      || globalForm.answer_mode !== mappingForm.answer_mode
      || globalForm.retrieval_hint !== mappingForm.retrieval_hint
      || globalForm.boost_weight !== mappingForm.boost_weight
    );
  }, [mappingForm, metadataMode, selectedGlobalMapping]);

  useEffect(() => {
    if (!editableMappings.length) {
      setSelectedMappingId(null);
      setMappingForm(EMPTY_FORM);
      return;
    }
    if (selectedMappingId && editableMappings.some(mapping => mapping._id === selectedMappingId)) return;
    selectMapping(editableMappings[0], { openModal: false });
  }, [editableMappings, selectedMappingId, selectMapping]);

  const filteredDiscovered = useMemo(() => {
    let list = discoveredFields;
    if (fieldSearch) {
      const q = fieldSearch.toLowerCase();
      list = list.filter(f => (f.path || '').toLowerCase().includes(q) || (f._collection || '').toLowerCase().includes(q));
    }
    if (activeFieldTab !== 'all') {
      list = list.filter(f => f._collection === activeFieldTab);
    }
    return list;
  }, [discoveredFields, fieldSearch, activeFieldTab]);

  const mappedPaths = useMemo(() => {
    const set = new Set();
    mappings.forEach(mapping => set.add(`${mapping.source_collection}.${mapping.source_field}`));
    return set;
  }, [mappings]);

  const mappingByPath = useMemo(() => {
    const map = new Map();
    mappings.forEach(mapping => {
      map.set(`${mapping.source_collection}.${mapping.source_field}`, mapping);
    });
    return map;
  }, [mappings]);

  const selectedFieldPreview = useMemo(() => {
    if (!selectedMapping) return '—';
    return previewByTargetField.get(selectedMapping.target_field) || '—';
  }, [previewByTargetField, selectedMapping]);

  const toggleMapping = useCallback(async (field) => {
    try {
      const res = await request.patch(`/studio/api/field-mappings/${field._id}/toggle`);
      setMappings(prev => prev.map(mapping => (
        mapping._id === field._id ? { ...mapping, enabled: res.data.enabled } : mapping
      )));
      setToastMessage(`${res.data.enabled ? 'Enabled' : 'Disabled'} ${field.target_field}.`);
    } catch (err) {
      console.error('Toggle mapping failed:', err);
    }
  }, []);

  const togglePolicy = useCallback(async (rule) => {
    try {
      const res = await request.patch(`/studio/api/policy-rules/${rule._id}/toggle`);
      setPolicyRules(prev => prev.map(item => (
        item._id === rule._id ? { ...item, enabled: res.data.enabled } : item
      )));
    } catch (err) {
      console.error('Toggle policy failed:', err);
    }
  }, []);

  const toggleHostAI = useCallback(async () => {
    if (!host) return;
    try {
      const res = await request.patch(`/studio/api/hosts/${host._id}/ai-toggle`);
      setHost(prev => ({ ...prev, aiConciergeEnabled: res.data.aiConciergeEnabled }));
    } catch (err) {
      console.error('Toggle AI failed:', err);
    }
  }, [host]);

  const toggleHostGuidebook = useCallback(async () => {
    if (!host) return;
    try {
      const res = await request.patch(`/studio/api/hosts/${host._id}/guidebook-toggle`);
      setHost(prev => ({ ...prev, guidebookEnabled: res.data.guidebookEnabled }));
    } catch (err) {
      console.error('Toggle Guidebook failed:', err);
    }
  }, [host]);

  const saveMapping = useCallback(async () => {
    if (!selectedMapping) return;
    setSavingMapping(true);
    try {
      const hostOverride = metadataMode === 'host';
      const payload = {
        source_collection: selectedMapping.source_collection,
        source_field: selectedMapping.source_field,
        target_table: selectedMapping.target_table,
        target_field: selectedMapping.target_field,
        host_id: hostOverride ? hostId : null,
        enabled: selectedMapping.enabled !== false,
        transform: selectedMapping.transform || null,
        display_label: mappingForm.display_label.trim() || null,
        retrieval_aliases: parseAliases(mappingForm.retrieval_aliases),
        retrieval_category: mappingForm.retrieval_category,
        answer_mode: mappingForm.answer_mode,
        retrieval_hint: mappingForm.retrieval_hint.trim() || null,
        boost_weight: Number(mappingForm.boost_weight || 2),
        guidebook_enabled: mappingForm.guidebook_enabled === true,
      };
      await request.post('/studio/api/field-mappings', payload);
      const refreshed = await loadMappings();
      const refreshedMatch = refreshed.find(mapping => mappingKey(mapping) === mappingKey(selectedMapping));
      if (refreshedMatch) {
        setSelectedMappingId(refreshedMatch._id);
        setMappingForm(buildFormState(refreshedMatch));
      }
    } catch (err) {
      console.error('Save mapping failed:', err);
    } finally {
      setSavingMapping(false);
    }
  }, [hostId, loadMappings, mappingForm, metadataMode, selectedMapping]);

  const addAllActiveFieldsToGuidebook = useCallback(async () => {
    setApplyingGuidebookAll(true);
    try {
      await request.post('/studio/api/field-mappings/guidebook/add-all-active', {
        hostId,
      });
      await loadMappings();
      setToastMessage('Applied guidebook inclusion to all active fields.');
    } catch (err) {
      console.error('Failed to add all active guidebook fields:', err);
    } finally {
      setApplyingGuidebookAll(false);
    }
  }, [hostId, loadMappings]);

  const removeFromGuidebook = useCallback(async (mapping) => {
    if (!mapping?._id) return;
    const confirmed = window.confirm(`Remove "${mapping.target_field}" from guidebook? This will not delete the field mapping.`);
    if (!confirmed) return;
    try {
      await request.patch(`/studio/api/field-mappings/${mapping._id}/guidebook-toggle`);
      await loadMappings();
      setToastMessage(`Removed ${mapping.target_field} from guidebook.`);
    } catch (err) {
      console.error('Failed to remove guidebook field:', err);
    }
  }, [loadMappings]);

  const deleteCustomField = useCallback(async (field) => {
    if (!field?.target_field) return;
    const confirmed = window.confirm(`Delete custom field "${field.target_field}" for this host? This cannot be undone.`);
    if (!confirmed) return;
    try {
      await request.delete(`/studio/api/hosts/${hostId}/custom-fields/${encodeURIComponent(field.target_field)}`);
      await Promise.all([loadCustomFields(), loadMappings()]);
      setToastMessage(`Deleted custom field ${field.target_field}.`);
    } catch (err) {
      console.error('Failed to delete custom field:', err);
    }
  }, [hostId, loadCustomFields, loadMappings]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center py-40">
        <div className="w-8 h-8 border-4 border-zinc-200 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!host) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center py-40 text-zinc-400">
        <p className="text-lg font-medium mb-2">Host not found</p>
        <button onClick={() => navigate('/ai-concierge')} className="text-sm text-indigo-600 hover:text-indigo-700">
          Back to AI Concierge
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <button
            onClick={() => navigate('/ai-concierge')}
            className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-700 transition-colors mb-4"
          >
            <FiChevronLeft className="w-4 h-4" /> AI Concierge
          </button>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-zinc-100 to-zinc-200 flex items-center justify-center text-lg font-bold text-zinc-600">
                {(host.firstName || '?')[0]}{(host.lastName || '?')[0]}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-zinc-900 tracking-tight">{host.firstName} {host.lastName}</h1>
                <p className="text-sm text-zinc-500">{host.email || 'No email'}</p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={toggleHostAI}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors ${
                  host.aiConciergeEnabled
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                    : 'bg-zinc-50 text-zinc-600 border-zinc-200 hover:bg-zinc-100'
                }`}
              >
                {host.aiConciergeEnabled ? <FiToggleRight className="w-5 h-5" /> : <FiToggleLeft className="w-5 h-5" />}
                {host.aiConciergeEnabled ? 'AI Active' : 'AI Disabled'}
              </button>
              <button
                onClick={toggleHostGuidebook}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors ${
                  host.guidebookEnabled
                    ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                    : 'bg-zinc-50 text-zinc-600 border-zinc-200 hover:bg-zinc-100'
                }`}
              >
                {host.guidebookEnabled ? <FiToggleRight className="w-5 h-5" /> : <FiToggleLeft className="w-5 h-5" />}
                {host.guidebookEnabled ? 'Guidebook Active' : 'Guidebook Disabled'}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-zinc-100 shadow-sm p-5 mb-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-base font-semibold text-zinc-900">Field Search</h2>
              <p className="text-xs text-zinc-500 mt-0.5">Search applies to active, inactive, guidebook, custom, and discovered fields.</p>
            </div>
            <div className="relative w-full sm:w-80">
              <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 w-3.5 h-3.5" />
              <input
                type="text"
                placeholder="Search fields..."
                value={fieldSearch}
                onChange={(e) => setFieldSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-indigo-300 outline-none transition-all"
              />
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {[
              { id: 'all', label: 'All' },
              { id: 'guidebook', label: 'Guidebook only' },
              { id: 'custom', label: 'Custom only' },
              { id: 'inactive', label: 'Unmapped/Inactive' },
            ].map((chip) => (
              <button
                key={chip.id}
                type="button"
                onClick={() => setSectionFilter(chip.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  sectionFilter === chip.id
                    ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                    : 'bg-zinc-50 text-zinc-500 border-zinc-200 hover:bg-zinc-100'
                }`}
              >
                {chip.label}
              </button>
            ))}
          </div>
          {toastMessage ? (
            <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
              {toastMessage}
            </div>
          ) : null}
        </div>

        {(sectionFilter === 'all' || sectionFilter === 'inactive') ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
            {sectionFilter === 'all' ? (
              <FieldCard
                title="Active Fields"
                badge="active"
                fields={activeFields}
                totalCount={activeFieldsAll.length}
                onToggle={toggleMapping}
                onSelect={selectMapping}
                selectedId={selectedMappingId}
                previewByTarget={previewByTargetField}
                showPreviewColumn
                emptyText={searchQuery ? 'No active fields match your search.' : 'No active fields in this category.'}
              />
            ) : null}
            <FieldCard
              title="Inactive Fields"
              badge="inactive"
              fields={inactiveFields}
              totalCount={inactiveFieldsAll.length}
              onToggle={toggleMapping}
              onSelect={selectMapping}
              selectedId={selectedMappingId}
              previewByTarget={previewByTargetField}
              emptyText={searchQuery ? 'No inactive fields match your search.' : 'No inactive fields in this category.'}
            />
            {sectionFilter === 'all' ? (
              <FieldCard
                title="Restricted Fields"
                badge="restricted"
                fields={restrictedFields}
                totalCount={policyRules.filter(rule => ['internal_only', 'secret_restricted'].includes(rule.exposure_level)).length}
                onToggle={togglePolicy}
                selectedId={null}
                emptyText={searchQuery ? 'No restricted fields match your search.' : 'No restricted fields in this category.'}
              />
            ) : null}
          </div>
        ) : null}

        {(sectionFilter === 'all' || sectionFilter === 'custom') ? (
          <div className="bg-white rounded-2xl border border-zinc-100 shadow-sm overflow-hidden mb-8">
            <div className="px-5 py-4 border-b border-zinc-100">
              <h2 className="text-base font-semibold text-zinc-900">Host Custom Fields</h2>
              <p className="text-xs text-zinc-500 mt-0.5">Custom fields only for this host. Deleting here removes only host custom mappings.</p>
            </div>
            {filteredCustomFields.length === 0 ? (
              <div className="px-5 py-8 text-sm text-zinc-400 text-center">
                {searchQuery ? 'No custom fields match your search.' : 'No host custom fields yet.'}
              </div>
            ) : (
              <div className="max-h-[220px] overflow-y-auto divide-y divide-zinc-50">
                {filteredCustomFields.map((field) => (
                  <div
                    key={`${field.host_id || 'host'}-${field.target_field}`}
                    className="px-5 py-3 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-zinc-800 truncate">{field.display_label || field.target_field}</p>
                      <p className="text-xs text-zinc-500 mt-0.5 truncate">
                        {field.target_field} · {field.guidebook_enabled ? 'Guidebook enabled' : 'Guidebook disabled'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => deleteCustomField(field)}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-red-200 text-red-700 bg-red-50 hover:bg-red-100"
                    >
                      <FiTrash2 className="w-3.5 h-3.5" />
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}

        <div className="bg-white rounded-2xl border border-zinc-100 shadow-sm p-5 mb-8">
          <h2 className="text-base font-semibold text-zinc-900">Mapped Field Metadata</h2>
          <p className="text-sm text-zinc-500 mt-1">
            Click any field in Active or Inactive Fields to open the metadata editor modal.
          </p>
          <div className="mt-4 max-w-sm">
            <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2">Preview Property</label>
            <select
              value={selectedPreviewPropertyId}
              onChange={(e) => setSelectedPreviewPropertyId(e.target.value)}
              className="w-full px-3 py-2.5 text-sm rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-indigo-300 outline-none transition-all"
            >
              {hostProperties.length === 0 ? (
                <option value="">No properties found</option>
              ) : (
                hostProperties.map((property) => (
                  <option key={property.uniqueId} value={property.uniqueId}>
                    {property.uniqueId} - {property.listingTitle}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>

        {(sectionFilter === 'all' || sectionFilter === 'guidebook') ? (
          <div className="bg-white rounded-2xl border border-zinc-100 shadow-sm overflow-hidden mb-8">
            <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-zinc-900">Guidebook Fields</h2>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Fields currently included in guidebook output for this host ({activeGuidebookFields.length}/{activeGuidebookFieldsAll.length}).
                </p>
              </div>
              <button
                type="button"
                onClick={addAllActiveFieldsToGuidebook}
                disabled={applyingGuidebookAll}
                className="px-3.5 py-2 rounded-lg text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 disabled:opacity-50"
              >
                {applyingGuidebookAll ? 'Applying...' : 'Add All from Active Fields'}
              </button>
            </div>
            {activeGuidebookFields.length === 0 ? (
              <div className="px-5 py-8 text-sm text-zinc-400 text-center">
                {searchQuery ? 'No guidebook fields match your search.' : 'No active guidebook fields yet.'}
              </div>
            ) : (
              <div className="max-h-[220px] overflow-y-auto divide-y divide-zinc-50">
                {activeGuidebookFields.map((field) => (
                  <div
                    key={field._id}
                    className="w-full text-left px-5 py-3 hover:bg-zinc-50 transition-colors flex items-center justify-between gap-3"
                  >
                    <button
                      type="button"
                      onClick={() => selectMapping(field)}
                      className="min-w-0 text-left"
                    >
                      <p className="text-sm font-medium text-zinc-800 truncate">{field.target_field}</p>
                      <p className="text-xs text-zinc-500 mt-0.5 truncate">{field.source_collection}.{field.source_field}</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => removeFromGuidebook(field)}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100"
                    >
                      <FiMinusCircle className="w-3.5 h-3.5" />
                      Remove from guidebook
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}

        {sectionFilter === 'all' ? (
          <div className="bg-white rounded-2xl border border-zinc-100 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-zinc-100">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-zinc-900 flex items-center gap-2">
                  <FiDatabase className="w-4 h-4 text-zinc-400" />
                  Available Fields
                </h2>
                <p className="text-xs text-zinc-500 mt-0.5">{discoveredFields.length} fields discovered across all collections</p>
              </div>
              <div className="flex gap-2 items-center">
                {['all', 'properties', 'rawdatas', 'hosts'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveFieldTab(tab)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors capitalize ${
                      activeFieldTab === tab
                        ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                        : 'bg-zinc-50 text-zinc-500 border border-zinc-200 hover:bg-zinc-100'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-zinc-50/95 backdrop-blur-sm">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Field Path</th>
                  <th className="text-left px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Type</th>
                  <th className="text-left px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Collection</th>
                  <th className="text-left px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">V1 Target</th>
                  <th className="text-center px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Mapped</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-50">
                {filteredDiscovered.map((field, index) => {
                  const pathKey = `${field._collection}.${field.path}`;
                  const isMapped = mappedPaths.has(pathKey);
                  const linkedMapping = mappingByPath.get(pathKey) || null;
                  return (
                    <tr
                      key={`${field._collection}-${field.path}-${index}`}
                      className={`transition-colors ${linkedMapping?._id === selectedMappingId ? 'bg-indigo-50/70' : 'hover:bg-zinc-50/60'}`}
                    >
                      <td className="px-5 py-3">
                        <button
                          type="button"
                          disabled={!linkedMapping}
                          onClick={() => linkedMapping && selectMapping(linkedMapping)}
                          className={`font-mono text-xs ${linkedMapping ? 'text-indigo-700 hover:text-indigo-900' : 'text-zinc-700'}`}
                        >
                          {field.path}
                        </button>
                      </td>
                      <td className="px-5 py-3 text-zinc-500 text-xs">{(field.types || []).join(', ') || '—'}</td>
                      <td className="px-5 py-3">
                        <span className="text-xs font-medium px-2 py-0.5 rounded-md bg-zinc-100 text-zinc-600">{field._collection}</span>
                      </td>
                      <td className="px-5 py-3 text-xs text-zinc-500">{field.suggested_v1_target || '—'}</td>
                      <td className="px-5 py-3 text-center">
                        {isMapped ? (
                          <button
                            type="button"
                            onClick={() => linkedMapping && selectMapping(linkedMapping)}
                            className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 hover:text-emerald-700"
                          >
                            <FiCheck className="w-3.5 h-3.5" /> Edit
                          </button>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-zinc-400">
                            <FiX className="w-3.5 h-3.5" /> No
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filteredDiscovered.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-12 text-center text-zinc-400 text-sm">
                      No fields found matching your filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="px-5 py-3 border-t border-zinc-100 bg-zinc-50/40 text-xs text-zinc-400">
            Showing {filteredDiscovered.length} of {discoveredFields.length} discovered fields
          </div>
          </div>
        ) : null}
      </div>

      {metadataModalOpen && selectedMapping ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4"
          onClick={() => setMetadataModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl border border-zinc-100 shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-base font-semibold text-zinc-900">Mapped Field Metadata</h2>
                <p className="text-xs text-zinc-500 mt-0.5">{selectedMapping.target_field}</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="inline-flex rounded-lg border border-zinc-200 p-0.5 bg-zinc-50">
                  <button
                    type="button"
                    onClick={() => setMetadataMode('global')}
                    className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                      metadataMode === 'global'
                        ? 'bg-white text-zinc-800 shadow-sm'
                        : 'text-zinc-500 hover:text-zinc-700'
                    }`}
                  >
                    Global
                  </button>
                  <button
                    type="button"
                    onClick={() => setMetadataMode('host')}
                    className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                      metadataMode === 'host'
                        ? 'bg-white text-zinc-800 shadow-sm'
                        : 'text-zinc-500 hover:text-zinc-700'
                    }`}
                  >
                    This Host
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setMetadataModalOpen(false)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1">Source</p>
                  <p className="text-sm text-zinc-700 font-mono">{selectedMapping.source_collection}.{selectedMapping.source_field}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1">Target</p>
                  <p className="text-sm text-zinc-700">{selectedMapping.target_table}.{selectedMapping.target_field}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1">Current Value Preview</p>
                  <p className="text-[11px] text-zinc-500 mb-2">
                    {selectedPreviewPropertyId ? `From property ${selectedPreviewPropertyId}` : 'Select a preview property above'}
                  </p>
                  <p className="text-sm text-zinc-700 bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2">
                    {selectedFieldPreview}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2">Display Label</label>
                  <input
                    type="text"
                    value={mappingForm.display_label}
                    onChange={(e) => setMappingForm(prev => ({ ...prev, display_label: e.target.value }))}
                    className="w-full px-3 py-2.5 text-sm rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-indigo-300 outline-none transition-all"
                    placeholder="Guest-facing label"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2">Retrieval Aliases</label>
                  <textarea
                    value={mappingForm.retrieval_aliases}
                    onChange={(e) => setMappingForm(prev => ({ ...prev, retrieval_aliases: e.target.value }))}
                    rows={4}
                    className="w-full px-3 py-2.5 text-sm rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-indigo-300 outline-none transition-all"
                    placeholder="Comma-separated synonyms"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2">Retrieval Hint</label>
                  <textarea
                    value={mappingForm.retrieval_hint}
                    onChange={(e) => setMappingForm(prev => ({ ...prev, retrieval_hint: e.target.value }))}
                    rows={4}
                    className="w-full px-3 py-2.5 text-sm rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-indigo-300 outline-none transition-all"
                    placeholder="Internal retrieval hint"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2">Retrieval Category</label>
                    <select
                      value={mappingForm.retrieval_category}
                      onChange={(e) => setMappingForm(prev => ({ ...prev, retrieval_category: e.target.value }))}
                      className="w-full px-3 py-2.5 text-sm rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-indigo-300 outline-none transition-all"
                    >
                      {['general', 'wifi', 'parking', 'checkin', 'checkout', 'rules', 'appliance', 'amenity', 'safety', 'troubleshooting', 'housekeeping', 'maintenance'].map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2">Answer Mode</label>
                    <select
                      value={mappingForm.answer_mode}
                      onChange={(e) => setMappingForm(prev => ({ ...prev, answer_mode: e.target.value }))}
                      className="w-full px-3 py-2.5 text-sm rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-indigo-300 outline-none transition-all"
                    >
                      {['exact', 'procedural', 'descriptive'].map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2">Boost Weight</label>
                  <input
                    type="number"
                    min="1"
                    max="5"
                    step="1"
                    value={mappingForm.boost_weight}
                    onChange={(e) => setMappingForm(prev => ({ ...prev, boost_weight: e.target.value }))}
                    className="w-full px-3 py-2.5 text-sm rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-indigo-300 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2">Guidebook Inclusion</label>
                  <label className="inline-flex items-center gap-2 text-sm text-zinc-700">
                    <input
                      type="checkbox"
                      checked={mappingForm.guidebook_enabled}
                      onChange={(e) => setMappingForm(prev => ({ ...prev, guidebook_enabled: e.target.checked }))}
                      className="rounded border-zinc-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    Include this field in guidebook sections
                  </label>
                </div>

                <div className="rounded-2xl border border-zinc-100 bg-zinc-50/70 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2">Effective Metadata Source</p>
                  <p className="text-sm text-zinc-600">
                    Effective source for this field: <span className="font-medium text-zinc-800">{selectedMapping._effectiveSource === 'host' ? 'Host override' : 'Global default'}</span>
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">
                    Host override exists: {selectedHostOverride ? 'Yes' : 'No'} · Global mapping exists: {selectedGlobalMapping ? 'Yes' : 'No'}
                  </p>
                  {hostOverrideDiverges ? (
                    <p className="text-xs text-amber-700 mt-2">
                      This host override currently diverges from the global baseline.
                    </p>
                  ) : null}
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <button
                    onClick={saveMapping}
                    disabled={savingMapping}
                    className="px-4 py-2.5 rounded-xl text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                  >
                    {savingMapping ? 'Saving...' : 'Save Metadata'}
                  </button>
                  <button
                    onClick={() => setMappingForm(buildFormState(selectedMapping))}
                    disabled={savingMapping}
                    className="px-4 py-2.5 rounded-xl text-sm font-medium bg-zinc-100 text-zinc-700 hover:bg-zinc-200 disabled:opacity-50 transition-colors"
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
